import axios from 'axios';
import VanguardConfig from '../../../config/vanguard';
import Utils from "../modules/utils";
import * as mutations from "@/app/store/mutations";

/**
 * Orion API
 */
export default class OrionApi {
  constructor(store, newAPI = false) {
    this.newApi = newAPI;
    this.baseHost = newAPI === false ? VanguardConfig.orionUrl : VanguardConfig.newOrionUrl;
    this.$store = store;
  }

  /**
   * POST new order (regular initial, regular cross-sell)
   * @param  {Object} data Orders data
   * @return {Promise}
   */
  postOrder(data) {
    return new Promise((resolve, reject) => {
      try {
        const url = this.baseHost + (this.newApi ? '/complete' : '/order');

        if (this.newApi) {
          data = this.formatDataForNewOrion(data);
        }
        axios.post(url, JSON.stringify(data), {
          headers: {
            'Content-Type': 'application/json'
          }
        }).then(response => {
          try {
            if (this.newApi) {
              resolve(response);
            } else {
              const data = JSON.parse(response.data);
              if (!data) {
                return reject(data);
              }

              resolve(data);
            }
          } catch (error) {
            reject(error);
          }
        }, error => {
          reject(error);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * PATCH data in existing order (up-sell, cancel)
   * @param  {Object} data Orders data
   * @return {Promise}     Resolve this schema {status: boolean, content: {}}
   */
  patchOrder(data) {
    return new Promise((resolve, reject) => {
      try {
        let url = this.baseHost + '/order';

        if (this.newApi && data.action === 'cancelled') {
          url = this.baseHost + '/cancel';
          data = this.formatCancelDataForNewOrion(data);

          axios.post(url, JSON.stringify(data), {
            headers: {
              'Content-Type': 'application/json'
            }
          }).then(response => {
            try {
              resolve(data);
            } catch (error) {
              reject(error);
            }
          }, error => {
            reject(error);
          });
        }
        else {
          axios.patch(url, JSON.stringify(data), {
            headers: {
              'Content-Type': 'application/json'
            }
          }).then(response => {
            try {
              const data = JSON.parse(response.data);
              if (!data) {
                return reject(data);
              }

              resolve(data);
            } catch (error) {
              reject(error);
            }
          }, error => {
            reject(error);
          });
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Check whether current customer has already performed an order for current choice.
   * Pass either sfId or email in function parameters. If both are passed, only sfId is considered server-side.
   *
   * @param {String} productId
   * @param {String} sfId
   * @param {String} email
   * @param {Boolean} _insert (optional) if true, insert order in Redis cache. Next call will return status=true => already ordered
   * @param {Boolean} _delete (optional) if true, delete order from Redis cache. Next call will return status=false => not ordered yet
   * @returns {Promise.<{status: boolean, content: Array}>}
   */
  getAlreadyOrdered(productId = '', sfId = '', email = '', _insert = false, _delete = false) {
    return new Promise((resolve, reject) => {
      try {
        let url = this.baseHost +
                    '/already?productId=' + encodeURIComponent(productId) +
                    '&sfId=' + encodeURIComponent(sfId) +
                    '&email=' + encodeURIComponent(email);
        if (_insert) {
          url += '&insert=1';
        } else if (_delete) {
          url += '&delete=1';
        }

        axios.get(url).then(response => {
          try {
            const data = JSON.parse(response.data);
            if (!data) {
              return reject(data);
            }

            resolve(data); // Data.status:true = already ordered, data.status:false = new order
          } catch (error) {
            reject(error);
          }
        }, error => {
          reject(error);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Check whether current customer has already performed an order for current choice.
   * Pass either sfId or email in function parameters. If both are passed, only sfId is considered server-side.
   *
   * @param {String} trackingCode
   * @param {String} sfId
   * @param {String} formCode
   * @param {String} paymentMethod
   * @param {String} choiceId
   * @param {String} productId
   * @returns {Promise.<{status: boolean, content: Array}>}
   */
  getAlreadyClicked(trackingCode, sfId, formCode, paymentMethod, choiceId, productId) {
    return new Promise((resolve, reject) => {
      this.$store.commit(mutations.SET_ALREADY_CLICKED_CHECKED, {checkInProgress: true});

      try {
        const url = this.baseHost +
            '/alreadyclicked?trackingCode=' + encodeURIComponent(trackingCode) +
            '&sfId=' + encodeURIComponent(sfId) +
            '&formCode=' + encodeURIComponent(formCode) +
            '&paymentType=' + encodeURIComponent(paymentMethod) +
            '&choiceId=' + encodeURIComponent(choiceId) +
            '&productId=' + encodeURIComponent(productId)
        ;

        axios.get(url).then(response => {
          try {
            const data = JSON.parse(response.data);
            if (!data) {
              return reject(data);
            }
            this.$store.commit(mutations.SET_ALREADY_CLICKED_CHECKED, data);

            resolve(data); // Data.status:true = already clicked, data.status:false = not clicked yet
          } catch (error) {
            reject(error);
          }
        }, error => {
          reject(error);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  postScreenshotBdc(orderId, imageData, isUpdate) {
    return new Promise((resolve, reject) => {
      const url = this.baseHost + '/screenshot/bdc';

      axios.post(url, {orderId, imageData, isUpdate}).then(response => {
        resolve(response);
      }).catch(error => {
        // We can't send the full error else we have an error with sentry (code HTTP 413 request too large)
        reject(error.message);
      });
    });
  }

  formatDataForNewOrion(data) {
    const newData = {
      cart: {
        items: []
      },
      coupons: {},
      order: {
        ...data.data,
        isPaymentCaptured: true,
        orderLines: undefined,
        waitingUrl: `${window.location.origin}/static/${data.data.lang}/waiting.html`,
      },
      domainId: 'vanguardv1',
      action: data.action
    };
    for (const orderLine in data.data.orderLines) {
      let shippingPrice = null;
      if (data.data.orderLines[orderLine].shippingConfig) {
        shippingPrice = data.data.orderLines[orderLine].shippingConfig[data.data.shippingCountry]
      }
      newData.cart.items.push({
        amount: Math.round(data.data.orderLines[orderLine].amount * 100), // We need to use this function to get the right amount e.g: 134.7*100 = 13469.99999 instead of 13470
        shippingPrice: shippingPrice || data.data.orderLines[orderLine].shippingPrice,
        salesforceId: data.data.orderLines[orderLine].productId,
        externalOrderLineId: Utils.generateUID(),
        choice: {
          id: data.data.orderLines[orderLine].choice,
          configUrl: (window.location.origin+window.location.pathname).replace(/\/[^\/]*$/, '/config.json')
        },
        qty: 1
      });
    }

    return newData;
  }

  formatCancelDataForNewOrion(data) {
    const newData = {
      order: {
        ...data.data
      },
      action: data.action
    };

    return newData;
  }
}



// WEBPACK FOOTER //
// ./src/common/api/orion-api.js